import type {
  CoachRuntimeServiceReadModel,
  CoachServiceCorrectionKind,
  CoachServiceCorrectionOperatorPreviewReadModel,
  CoachServiceCorrectionOperatorResult,
  PreviewCoachServiceCorrectionRequest,
} from "@billiard/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Clock3, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { OperatorIdentity } from "./api";
import {
  type CoachServiceCorrectionCommand,
  coachServiceCorrectionFailureMessage,
  coachServiceCorrectionOutcomeIsUnknown,
  executeCoachServiceCorrection,
  previewCoachServiceCorrection,
} from "./coachServiceCorrectionApi";
import {
  beginCoachServiceCorrectionDispatch,
  type CoachServiceCorrectionRecovery,
  claimCoachServiceCorrectionRecovery,
  clearCoachServiceCorrectionRecovery,
  coachServiceCorrectionContext,
  coachServiceCorrectionRecoveryBlocksWrites,
  finishCoachServiceCorrectionDispatch,
  markCoachServiceCorrectionOutcomeUnknown,
  readCoachServiceCorrectionRecovery,
  resolveCoachServiceCorrectionAuthority,
} from "./coachServiceCorrectionRecovery";

type OperatorRole = OperatorIdentity["role"];

const correctionLabels: Record<CoachServiceCorrectionKind, string> = {
  MISASSIGN_CANCEL: "误派撤销",
  LATE_START_ADJUST: "迟到调时",
};

function fee(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

function dateTime(value: string | null): string {
  return value ? new Date(value).toLocaleString("zh-CN", { hour12: false }) : "尚未上钟";
}

function localDateTime(value: string): string {
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export function CoachServiceCorrectionPanel({
  operator,
  effectiveRoles,
  selectedService,
  selectedCoachDisplayName,
  blocked,
  onRecoveryViewChange,
  onCompleted,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  selectedService: CoachRuntimeServiceReadModel | undefined;
  selectedCoachDisplayName: string | undefined;
  blocked: boolean;
  onRecoveryViewChange?: (view: ReturnType<typeof readCoachServiceCorrectionRecovery>) => void;
  onCompleted: () => void | Promise<void>;
}): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const ownerReadOnly = operator.role === "OWNER" || effectiveRoles.includes("OWNER");
  const canCorrect =
    !ownerReadOnly &&
    (operator.role === "MANAGER" ||
      operator.role === "CASHIER" ||
      effectiveRoles.includes("MANAGER") ||
      effectiveRoles.includes("CASHIER"));
  const context = useMemo(
    () =>
      coachServiceCorrectionContext({
        id: operator.id,
        tenantId: operator.tenantId,
        storeId: operator.storeId,
      }),
    [operator.id, operator.storeId, operator.tenantId],
  );
  const contextKey = `${context.tenantId}:${context.storeId}:${context.actorId}`;
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;
  const [kind, setKind] = useState<CoachServiceCorrectionKind>("MISASSIGN_CANCEL");
  const [reason, setReason] = useState("");
  const [correctedStartedAt, setCorrectedStartedAt] = useState("");
  const [preview, setPreview] = useState<CoachServiceCorrectionOperatorPreviewReadModel | null>(
    null,
  );
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState<CoachServiceCorrectionOperatorResult | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [previewClock, setPreviewClock] = useState(() => Date.now());
  const [recoveryView, setRecoveryViewState] = useState(() =>
    readCoachServiceCorrectionRecovery(context),
  );
  const setRecoveryView = useCallback(
    (view: ReturnType<typeof readCoachServiceCorrectionRecovery>): void => {
      setRecoveryViewState(view);
      onRecoveryViewChange?.(view);
    },
    [onRecoveryViewChange],
  );
  const probingRef = useRef<string | null>(null);

  const refresh = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: ["coach-runtime"] });
    void queryClient.invalidateQueries({ queryKey: ["coach-summary"] });
    void queryClient.invalidateQueries({ queryKey: ["coach-monthly-commission"] });
    void queryClient.invalidateQueries({ queryKey: ["coach-service-correction-notices"] });
    void onCompleted();
  }, [onCompleted, queryClient]);

  const executeMutation = useMutation({
    mutationFn: async ({
      recovery,
      expectedContextKey,
    }: {
      recovery: CoachServiceCorrectionRecovery;
      expectedContextKey: string;
    }) => {
      try {
        const corrected = await executeCoachServiceCorrection(recovery.command);
        if (contextKeyRef.current !== expectedContextKey) {
          throw new Error("操作期间账号或门店发生变化，必须先核对原请求结果");
        }
        return corrected;
      } catch (error) {
        if (coachServiceCorrectionOutcomeIsUnknown(error)) {
          markCoachServiceCorrectionOutcomeUnknown(recovery, context);
        } else {
          clearCoachServiceCorrectionRecovery(recovery, context);
        }
        throw error;
      } finally {
        finishCoachServiceCorrectionDispatch(recovery, context);
      }
    },
    onSuccess: (corrected, variables) => {
      if (!clearCoachServiceCorrectionRecovery(variables.recovery, context)) {
        setFailure("纠正已经返回结果，但本机无法清除原请求记录。为防止重复纠正，当前继续锁定。");
        setRecoveryView(readCoachServiceCorrectionRecovery(context));
        return;
      }
      setRecoveryView(readCoachServiceCorrectionRecovery(context));
      setResult(corrected);
      setPreview(null);
      setConfirmed(false);
      setFailure(null);
      setFeedback(
        corrected.kind === "MISASSIGN_CANCEL"
          ? "错误服务已结束；本次不计服务费、不产生提成，并已生成助教通知。"
          : "开始时间已修正；服务继续进行，后续下钟会从新时间计算服务费和提成。",
      );
      refresh();
    },
    onError: (error) => {
      setRecoveryView(readCoachServiceCorrectionRecovery(context));
      if (coachServiceCorrectionOutcomeIsUnknown(error)) {
        setFailure("纠正结果暂时无法确认。系统已锁定原请求并先查询门店结果，请勿再次提交。");
      } else {
        setFailure(`${coachServiceCorrectionFailureMessage(error)} 请按提示修改后重新预览。`);
      }
    },
  });

  const dispatchRecovery = useCallback(
    (recovery: CoachServiceCorrectionRecovery): void => {
      if (!beginCoachServiceCorrectionDispatch(recovery, context)) {
        setRecoveryView(readCoachServiceCorrectionRecovery(context));
        setFailure("本机无法安全读取或锁定原纠正请求，系统没有执行。请勿重复提交。 ");
        return;
      }
      executeMutation.mutate({ recovery, expectedContextKey: contextKey });
    },
    [context, contextKey, executeMutation, setRecoveryView],
  );

  const previewMutation = useMutation({
    mutationFn: async (request: PreviewCoachServiceCorrectionRequest) => {
      if (!selectedService) throw new Error("请先选择一条待上钟或服务中的助教任务");
      return previewCoachServiceCorrection(context, selectedService, request);
    },
    onSuccess: (nextPreview) => {
      setPreview(nextPreview);
      setConfirmed(false);
      setResult(null);
      setFailure(null);
      setFeedback(
        nextPreview.canExecute
          ? "预览已生成。请逐项核对时间、分钟、费用、提成和实际后果，再勾选确认。"
          : (nextPreview.blocker ?? "当前不能执行，请按页面说明修改。"),
      );
    },
    onError: (error) => {
      setPreview(null);
      setConfirmed(false);
      setFeedback(null);
      setFailure(`${coachServiceCorrectionFailureMessage(error)} 请刷新后重新选择服务。`);
    },
  });

  useEffect(() => {
    if (!selectedService) {
      setPreview(null);
      setConfirmed(false);
      return;
    }
    if (kind === "LATE_START_ADJUST" && selectedService.status !== "ACTIVE") {
      setKind("MISASSIGN_CANCEL");
    }
    if (
      preview &&
      (preview.serviceId !== selectedService.id ||
        preview.expectedServiceVersion !== selectedService.version ||
        preview.serviceStatus !== selectedService.status ||
        preview.originalStartedAt !== selectedService.startedAt)
    ) {
      setPreview(null);
      setConfirmed(false);
      setFeedback(null);
      setFailure("服务状态或版本已经变化，旧预览和确认已撤销。请重新生成预览后再执行。");
    }
  }, [kind, preview, selectedService]);

  useEffect(() => {
    if (!preview) return;
    const remaining = Date.parse(preview.generatedAt) + 5 * 60_000 - Date.now();
    if (remaining <= 0) {
      setPreviewClock(Date.now());
      setConfirmed(false);
      setFailure("费用和提成预览已超过 5 分钟。输入内容已保留，请重新生成预览并再次确认。 ");
      return;
    }
    const timeout = window.setTimeout(() => {
      setPreviewClock(Date.now());
      setConfirmed(false);
      setFailure("费用和提成预览已超过 5 分钟。输入内容已保留，请重新生成预览并再次确认。 ");
    }, remaining);
    return () => window.clearTimeout(timeout);
  }, [preview]);

  useEffect(() => {
    const view = readCoachServiceCorrectionRecovery(context);
    setRecoveryView(view);
    if (view.status === "CONTEXT_MISMATCH") {
      setFailure(
        "另一账号或门店有一笔助教纠正尚未核清。当前账号不能查看原内容，也不能发起新纠正。",
      );
    } else if (view.status === "STORAGE_UNAVAILABLE") {
      setFailure("浏览器无法安全读取助教纠正恢复记录。为防止重复扣费或改时，纠正操作已停止。");
    }
  }, [context, setRecoveryView]);

  useEffect(() => {
    if (recoveryView.status !== "OWNED" || recoveryView.recovery.state !== "OUTCOME_UNKNOWN") {
      return;
    }
    const recovery = recoveryView.recovery;
    if (probingRef.current === recovery.command.request.idempotencyKey) return;
    probingRef.current = recovery.command.request.idempotencyKey;
    setFeedback("正在按原请求号查询门店结果；查询清楚前不会重新纠正。 ");
    void resolveCoachServiceCorrectionAuthority(recovery, context)
      .then((resolution) => {
        setRecoveryView(readCoachServiceCorrectionRecovery(context));
        if (resolution.status === "EXECUTED") {
          setResult(resolution.result);
          setFailure(null);
          setFeedback("已确认原纠正请求执行成功，页面已按权威结果刷新。");
          refresh();
        } else if (resolution.status === "RETRY_ALLOWED") {
          setFailure(null);
          setFeedback(
            "门店明确确认原请求没有执行。可使用原账号、原预览、原内容和原请求号安全重试。",
          );
        } else if (resolution.status === "NOT_EXECUTED_REFRESH_REQUIRED") {
          setKind(resolution.command.request.kind);
          setReason(resolution.command.request.reason);
          setCorrectedStartedAt(
            resolution.command.request.correctedStartedAt
              ? localDateTime(resolution.command.request.correctedStartedAt)
              : "",
          );
          setPreview(null);
          setConfirmed(false);
          setFailure(null);
          setFeedback(
            "门店已确认原请求没有执行，但原费用和提成预览已超过 5 分钟。输入内容已保留，请重新生成预览。",
          );
        } else {
          setFailure("原纠正结果仍无法可靠确认，当前继续锁定且不会自动重发。请恢复连接后刷新。 ");
        }
      })
      .catch((error) => {
        setRecoveryView(readCoachServiceCorrectionRecovery(context));
        setFailure(`${coachServiceCorrectionFailureMessage(error)} 当前继续锁定，不会自动重发。`);
      })
      .finally(() => {
        probingRef.current = null;
      });
  }, [context, recoveryView, refresh, setRecoveryView]);

  if (!canCorrect && !ownerReadOnly) return null;

  const recoveryLocked =
    recoveryView.status !== "NONE" || coachServiceCorrectionRecoveryBlocksWrites();
  const pending = previewMutation.isPending || executeMutation.isPending;
  const previewExpired =
    preview !== null && previewClock >= Date.parse(preview.generatedAt) + 5 * 60_000;
  const changedInput = (): void => {
    setPreview(null);
    setConfirmed(false);
    setResult(null);
    setFeedback(null);
  };

  const requestPreview = (): void => {
    if (!selectedService) return;
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 2 || trimmedReason.length > 500) {
      setFailure("请填写 2 至 500 个字的具体原因，例如“误选助教”或“助教实际迟到 12 分钟”。");
      return;
    }
    if (kind === "LATE_START_ADJUST") {
      const corrected = Date.parse(correctedStartedAt);
      const original = Date.parse(selectedService.startedAt ?? "");
      if (!Number.isFinite(corrected) || !Number.isFinite(original) || corrected <= original) {
        setFailure("新开始时间必须晚于原开始时间；请按助教实际到桌时间重新选择。 ");
        return;
      }
      if (corrected > Date.now()) {
        setFailure("新开始时间不能晚于现在；请填写已经实际发生的到桌时间。 ");
        return;
      }
    }
    setFailure(null);
    previewMutation.mutate(
      kind === "LATE_START_ADJUST"
        ? {
            kind: "LATE_START_ADJUST",
            correctedStartedAt: new Date(correctedStartedAt).toISOString(),
            reason: trimmedReason,
          }
        : { kind: "MISASSIGN_CANCEL", correctedStartedAt: null, reason: trimmedReason },
    );
  };

  const execute = (): void => {
    if (!preview || !confirmed || !preview.canExecute) return;
    if (preview.kind === "LATE_START_ADJUST" && preview.correctedStartedAt === null) {
      setFailure("迟到调时预览缺少实际开始时间，系统没有执行。请重新生成预览。 ");
      return;
    }
    const previewCorrectedStartedAt = preview.correctedStartedAt;
    const request: CoachServiceCorrectionCommand["request"] =
      preview.kind === "LATE_START_ADJUST"
        ? {
            kind: "LATE_START_ADJUST",
            correctedStartedAt: previewCorrectedStartedAt as string,
            expectedServiceVersion: preview.expectedServiceVersion,
            reason: preview.reason,
            correctionToken: preview.correctionToken,
            previewGeneratedAt: preview.generatedAt,
            idempotencyKey: crypto.randomUUID(),
          }
        : {
            kind: "MISASSIGN_CANCEL",
            correctedStartedAt: null,
            expectedServiceVersion: preview.expectedServiceVersion,
            reason: preview.reason,
            correctionToken: preview.correctionToken,
            previewGeneratedAt: preview.generatedAt,
            idempotencyKey: crypto.randomUUID(),
          };
    const command: CoachServiceCorrectionCommand = {
      context,
      preview,
      request,
    };
    const claimed = claimCoachServiceCorrectionRecovery(context, command);
    setRecoveryView(claimed);
    if (claimed.status === "OWNED") {
      dispatchRecovery(claimed.recovery);
    } else {
      setFailure("本机已有一笔纠正尚未核清，系统没有发送新请求。请按恢复提示处理。 ");
    }
  };

  return (
    <section className="coach-correction-panel" aria-label="助教服务纠正">
      {canCorrect || ownerReadOnly ? (
        <>
          <div className="coach-section-title">
            <div>
              <span>更正误派或实际迟到，不用于普通上下钟</span>
              <h2>服务纠正</h2>
            </div>
            <RotateCcw size={21} aria-hidden="true" />
          </div>
          {ownerReadOnly ? (
            <div className="coach-correction-readonly">
              老板账号在此只读。服务纠正会改变费用和提成，请由当班店长或收银员核对后执行。
            </div>
          ) : !selectedService ? (
            <p className="coach-empty">请先在上方“当前任务”中选择一条待上钟或服务中的助教服务。</p>
          ) : (
            <div className="coach-correction-workspace">
              <div className="coach-correction-service">
                <strong>
                  {selectedService.tableNumber} 号桌 · {selectedService.serviceTypeName}
                </strong>
                <span>助教：{selectedCoachDisplayName ?? "姓名读取中"}</span>
                <span>当前状态：{selectedService.status === "ASSIGNED" ? "待上钟" : "服务中"}</span>
                <span>原开始时间：{dateTime(selectedService.startedAt)}</span>
              </div>
              <fieldset disabled={blocked || recoveryLocked || pending}>
                <legend>纠正类型</legend>
                <label>
                  <input
                    type="radio"
                    name="coach-correction-kind"
                    checked={kind === "MISASSIGN_CANCEL"}
                    onChange={() => {
                      setKind("MISASSIGN_CANCEL");
                      changedInput();
                    }}
                  />
                  <span>
                    <strong>误派撤销</strong>
                    ：结束这条错误服务；本次不计服务费、不产生提成，并通知助教。
                  </span>
                </label>
                {selectedService.status === "ACTIVE" ? (
                  <label>
                    <input
                      type="radio"
                      name="coach-correction-kind"
                      checked={kind === "LATE_START_ADJUST"}
                      onChange={() => {
                        setKind("LATE_START_ADJUST");
                        if (!correctedStartedAt && selectedService.startedAt) {
                          setCorrectedStartedAt(localDateTime(selectedService.startedAt));
                        }
                        changedInput();
                      }}
                    />
                    <span>
                      <strong>迟到调时</strong>
                      ：服务继续进行，只把开始时间改为实际到桌时间，并重算分钟、费用和提成。
                    </span>
                  </label>
                ) : null}
              </fieldset>
              {kind === "LATE_START_ADJUST" ? (
                <label>
                  实际开始时间
                  <input
                    type="datetime-local"
                    value={correctedStartedAt}
                    disabled={blocked || recoveryLocked || pending}
                    onChange={(event) => {
                      setCorrectedStartedAt(event.target.value);
                      changedInput();
                    }}
                  />
                  <span>必须晚于原开始时间且不晚于现在。以助教实际到桌并开始服务的时间为准。</span>
                </label>
              ) : null}
              <label>
                纠正原因
                <textarea
                  value={reason}
                  maxLength={500}
                  disabled={blocked || recoveryLocked || pending}
                  placeholder="例如：收银员误选了助教；助教实际 19:12 才到桌"
                  onChange={(event) => {
                    setReason(event.target.value);
                    changedInput();
                  }}
                />
                <span>必须填写 2 至 500 字；原因会保留在纠正记录和助教通知中。</span>
              </label>
              <button
                className="button secondary"
                type="button"
                disabled={blocked || recoveryLocked || pending}
                onClick={requestPreview}
              >
                <Clock3 size={16} aria-hidden="true" /> 生成纠正后果预览
              </button>
              {preview ? (
                <div className="coach-correction-preview">
                  <div>
                    <strong>
                      {preview.coachDisplayName} · {preview.tableNumber} 号桌
                    </strong>
                    <span>{preview.serviceTypeName}</span>
                    <span>原开始：{dateTime(preview.originalStartedAt)}</span>
                    <span>新开始：{dateTime(preview.correctedStartedAt)}</span>
                    <span>
                      此费用/提成预览以 {dateTime(preview.generatedAt)} 为准，5 分钟内有效。
                    </span>
                  </div>
                  <div className="coach-correction-effects">
                    <div>
                      <span>截至预览时刻预计分钟</span>
                      <strong>
                        {preview.before.billedMinutes} → {preview.after.billedMinutes}
                      </strong>
                    </div>
                    <div>
                      <span>截至预览时刻预计服务费</span>
                      <strong>
                        {fee(preview.before.serviceFeeFen)} → {fee(preview.after.serviceFeeFen)}
                      </strong>
                    </div>
                    <div>
                      <span>截至预览时刻预计提成</span>
                      <strong>
                        {preview.before.commissionFen !== null &&
                        preview.after.commissionFen !== null
                          ? `${fee(preview.before.commissionFen)} → ${fee(preview.after.commissionFen)}`
                          : "金额仅老板/店长可见"}
                      </strong>
                    </div>
                  </div>
                  <ul>
                    {preview.consequences.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  {preview.blocker ? <div className="error-band">{preview.blocker}</div> : null}
                  <label className="coach-correction-confirmation">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      disabled={
                        !preview.canExecute ||
                        previewExpired ||
                        blocked ||
                        recoveryLocked ||
                        pending
                      }
                      onChange={(event) => setConfirmed(event.target.checked)}
                    />
                    <span>
                      我已核对助教、球桌、原/新时间、分钟、费用
                      {preview.before.commissionFen === null
                        ? "，并了解提成会按门店规则同步重算"
                        : "、提成"}
                      和上述后果，确认执行。
                    </span>
                  </label>
                  <button
                    className="button danger"
                    type="button"
                    disabled={
                      !confirmed ||
                      !preview.canExecute ||
                      previewExpired ||
                      blocked ||
                      recoveryLocked ||
                      pending
                    }
                    onClick={execute}
                  >
                    <RotateCcw size={16} aria-hidden="true" /> 确认执行
                    {correctionLabels[preview.kind]}
                  </button>
                </div>
              ) : null}
              {result ? (
                <output className="success-band coach-correction-result" aria-live="polite">
                  <Check size={17} aria-hidden="true" />
                  <span>
                    <strong>{correctionLabels[result.kind]}已完成</strong>
                    编号 {result.correctionNumber}；截至纠正时刻的对比估算为{" "}
                    {result.after.billedMinutes} 分钟、服务费 {fee(result.after.serviceFeeFen)}
                    、提成{" "}
                    {result.after.commissionFen === null
                      ? "金额仅老板/店长可见"
                      : fee(result.after.commissionFen)}
                    ，不表示已经结费入账。助教通知已生成。
                  </span>
                </output>
              ) : null}
            </div>
          )}
          {recoveryLocked ? (
            <div className="coach-correction-recovery" role="alert">
              <AlertTriangle size={17} aria-hidden="true" />
              <span>上一笔助教纠正仍在核对。结果明确前不能生成新预览或提交新纠正。</span>
              {recoveryView.status === "OWNED" && recoveryView.canRetry ? (
                <button
                  className="button secondary"
                  type="button"
                  disabled={pending}
                  onClick={() => dispatchRecovery(recoveryView.recovery)}
                >
                  使用原请求安全重试
                </button>
              ) : null}
            </div>
          ) : null}
          {feedback ? <output className="success-band">{feedback}</output> : null}
          {failure ? (
            <div className="error-band" role="alert">
              {failure}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
